import { useCallback, useEffect, useState } from 'react';
import {
  databaseService,
  type DiagnosticsPolicy,
  type DiagEnforcement,
} from '../services/database';

interface Props {
  roomId: string;
  roomName: string;
}

// Room-scoped diagnostics policy (operations.diagnostic_config). Unlike the
// device toggles above it, these are DB-backed and apply to every PC in the room,
// so `block` (which lets PCs restart services and reboot themselves) is gated
// behind an inline confirm rather than flipping on the first tap. Inline, not
// window.confirm — that is unreliable in the Electron app and awkward on the
// touch screen this runs on.
export default function RoomDiagnosticsSettings({ roomId, roomName }: Props) {
  const [policy, setPolicy] = useState<DiagnosticsPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingBlock, setConfirmingBlock] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const p = await databaseService.getDiagnosticsPolicy();
    setError(p ? null : 'Could not load diagnostics policy.');
    setPolicy(p);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Close a pending confirm if the operator switches rooms mid-flow.
  useEffect(() => {
    setConfirmingBlock(false);
  }, [roomId]);

  if (loading) {
    return (
      <section className="settings-room">
        <div className="settings-room-header">This room — {roomName}</div>
        <p className="settings-hint">Loading…</p>
      </section>
    );
  }

  if (!policy) {
    return (
      <section className="settings-room">
        <div className="settings-room-header">This room — {roomName}</div>
        <p className="settings-error">{error ?? 'Unavailable.'}</p>
      </section>
    );
  }

  const enfOverride = policy.enforcement.rooms[roomId];
  const effectiveEnf: DiagEnforcement = enfOverride ?? policy.enforcement.default;
  const autoOverride = policy.autoRun.rooms[roomId];
  const effectiveAuto = autoOverride ?? policy.autoRun.default;

  const applyEnforcement = async (value: DiagEnforcement) => {
    setConfirmingBlock(false);
    if (value === effectiveEnf) return;
    setSaving(true);
    const ok = await databaseService.setRoomEnforcement(roomId, value);
    if (!ok) setError('Could not save enforcement.');
    await load();
    setSaving(false);
  };

  const onEnforcementClick = (value: DiagEnforcement) => {
    if (value === 'block' && effectiveEnf !== 'block') {
      setConfirmingBlock(true); // escalation → confirm first
      return;
    }
    void applyEnforcement(value);
  };

  const onAutoRunToggle = async (enabled: boolean) => {
    setSaving(true);
    const ok = await databaseService.setRoomAutoRun(roomId, enabled);
    if (!ok) setError('Could not save auto-run.');
    await load();
    setSaving(false);
  };

  return (
    <section className="settings-room">
      <div className="settings-room-header">This room — {roomName}</div>
      <p className="settings-hint">
        These apply to every PC in {roomName}, not just this device.
      </p>

      {error && <p className="settings-error">{error}</p>}

      {/* Enforcement */}
      <div className="settings-item settings-item--static">
        <div className="settings-item-text">
          <div className="settings-item-label">Auto-repair enforcement</div>
          <div className="settings-item-desc">
            <strong>Report only</strong> detects and logs faults without acting.{' '}
            <strong>Block</strong> lets PCs restart services and, on an idle PC,
            reboot themselves to self-repair — and blocks launches on a PC judged
            malfunctioning.
            <br />
            Fleet default: <strong>{policy.enforcement.default}</strong>
            {enfOverride === undefined ? ' (inherited)' : ' · room override active'}
          </div>
        </div>
        <div className="settings-segment" role="group" aria-label="Enforcement">
          <button
            type="button"
            className={`settings-segment-btn${effectiveEnf === 'report_only' ? ' is-active' : ''}`}
            disabled={saving}
            onClick={() => onEnforcementClick('report_only')}
          >
            Report only
          </button>
          <button
            type="button"
            className={`settings-segment-btn settings-segment-btn--danger${effectiveEnf === 'block' ? ' is-active' : ''}`}
            disabled={saving}
            onClick={() => onEnforcementClick('block')}
          >
            Block
          </button>
        </div>
      </div>

      {confirmingBlock && (
        <div className="settings-confirm">
          <div className="settings-confirm-text">
            Enable <strong>Block</strong> for {roomName}? PCs in this room will be
            able to restart services and reboot themselves to self-repair.
          </div>
          <div className="settings-confirm-actions">
            <button
              type="button"
              className="settings-btn settings-btn--danger"
              disabled={saving}
              onClick={() => void applyEnforcement('block')}
            >
              Enable block
            </button>
            <button
              type="button"
              className="settings-btn"
              disabled={saving}
              onClick={() => setConfirmingBlock(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Auto-run */}
      <label className="settings-item" htmlFor="setting-room-autorun">
        <div className="settings-item-text">
          <div className="settings-item-label">Automatic diagnostic runs</div>
          <div className="settings-item-desc">
            Run the boot / daily self-test cycle automatically. When off, only
            manual and condition-triggered runs execute.
            <br />
            Fleet default: <strong>{policy.autoRun.default ? 'on' : 'off'}</strong>
            {autoOverride === undefined ? ' (inherited)' : ' · room override active'}
          </div>
        </div>
        <span className="settings-toggle">
          <input
            id="setting-room-autorun"
            type="checkbox"
            checked={effectiveAuto}
            disabled={saving}
            onChange={(e) => void onAutoRunToggle(e.target.checked)}
          />
          <span className="settings-toggle-slider" aria-hidden="true" />
        </span>
      </label>
    </section>
  );
}
